import { StockForm, StockTable, RefreshControls } from '../components/stocks';
import { MarketIndicators, CurrencyRates } from '../components/market';
import { fetchStockData } from '../api/api';
import React, { useState, useEffect, useCallback } from 'react';
import '../styles.css';
import { useStocks } from '../hooks/useStocks';
import { ErrorBoundary } from '../components/common';

export default function StockTrackerPage() {

    const {
        stocks,
        loading: stocksLoading,
        addStock,
        removeStock,
        refreshStock,
        editStock
    } = useStocks();

    const [refreshInterval, setRefreshInterval] = useState(300000);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [refreshStates, setRefreshStates] = useState({});
    const [showWatchlistOnly, setShowWatchlistOnly] = useState(false);

    const refreshAllStocks = useCallback(async () => {
        if (isRefreshing) return;
        setIsRefreshing(true);
        setRefreshStates(Object.fromEntries(
            stocks.map(stock => [stock.url, 'pending'])
        ));

        try {
            await Promise.all(
                stocks.map(async (stock) => {
                    try {
                        // 1. Update price data
                        const priceData = await fetchStockData(stock.url);

                        // 2. Update enhanced metrics
                        setRefreshStates(prev => ({...prev, [stock.url]: 'updating-enhanced'}));
                        //const enhancedData = await refreshEnhancedData(stock); //enhancedData n'est pas utilise

                        // 3. Update in database
                        await refreshStock(stock.id, priceData);

                        // 4. Mark as completed
                        setRefreshStates(prev => ({...prev, [stock.url]: 'completed'}));
                    } catch (error) {
                        setRefreshStates(prev => ({...prev, [stock.url]: 'error'}));
                        console.error(`Failed to refresh ${stock.url}:`, error);
                    }
                })
            );
        } finally {
            setTimeout(() => {
                setRefreshStates({});
                setIsRefreshing(false);
            }, 1000);
        }
    }, [isRefreshing, stocks, refreshStock]);

    useEffect(() => {
        if (refreshInterval <= 0) return; // Skip if refresh is off (0)
        const intervalId = setInterval(refreshAllStocks, refreshInterval);
        return () => clearInterval(intervalId);
    }, [refreshInterval, refreshAllStocks]);

    if (stocksLoading) {
        return <div>Loading your stocks...</div>;
    }


    const handleAddStock = async (url) => {
        try {
            // Check if stock already exists
            if (stocks.some(stock => stock.url === url)) {
                alert("This stock is already being tracked!");
                return;
            }

            // First get stock data
            const stockData = await fetchStockData(url);

            // Then save to database
            await addStock({
                url: url,
                name: stockData.name,
                isin: stockData.isin,
                price: stockData.price,
                objective: "",
                watchlist: false,
                daily_change: stockData.daily_change,
                five_days_change: stockData.five_days_change,
                created_at: new Date().toLocaleString()
            });
        } catch (error) {
            alert(`Error: ${error.message}`);
        }
    };

//commente car pas utilisé
//     const refreshEnhancedData = async (stock) => {
//         try {
//             const metrics = await fetchEnhancedMetrics(stock.url);
//             return metrics;
//         } catch (error) {
//             console.error(`Failed to refresh ${stock.url}:`, error);
//             return {};
//         }
//     };


    const filteredStocks = showWatchlistOnly
        ? stocks.filter(stock => stock.watchlist)
        : stocks;

    return (
        <div className="App">
            <h1>Bourse Direct Stock Tracker</h1>
            <div className="top-indicators-container">
                <ErrorBoundary>
                    <MarketIndicators />
                </ErrorBoundary>
                <CurrencyRates />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                <ErrorBoundary>
                    <StockForm onAdd={handleAddStock} />
                </ErrorBoundary>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <input
                        type="checkbox"
                        checked={showWatchlistOnly}
                        onChange={() => setShowWatchlistOnly(!showWatchlistOnly)}
                    />
                    Watchlist only
                </label>
                {showWatchlistOnly && (
                  <p className="stock-counter">
                    Showing {filteredStocks.length}/{stocks.length} stocks
                  </p>
                )}
            </div>
            <ErrorBoundary>
                <RefreshControls
                    isRefreshing={isRefreshing}
                    refreshAllStocks={refreshAllStocks}
                    status={
                        isRefreshing ?
                        `${Object.values(refreshStates).filter(s => s === 'completed').length}/${stocks.length} updated` :
                        null
                    }
                    refreshInterval={refreshInterval}
                    setRefreshInterval={setRefreshInterval}
                    stocks_length={stocks.length}
                />
            </ErrorBoundary>
            <ErrorBoundary>
                <StockTable
                    stocks={filteredStocks}
                    removeStock={removeStock}
                    toggleWatchlist={(id, currentValue) => editStock(id, { watchlist: !currentValue })}
                    updateObjective={(id, objective) => editStock(id, { objective })}
                    isRefreshing={isRefreshing}
                    refreshStates={refreshStates}
                    editStock={editStock}
                />
            </ErrorBoundary>

        </div>
    );
}
